import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Copy, Share2, Check, Users, Gift } from "lucide-react";

export default function ClientReferralsPage() {
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => { api.get("/me/referrals").then(({ data }) => setData(data)); }, []);
    if (!data) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

    const link = `${window.location.origin}/join/${data.coach_code}?via=${data.referrer_id}`;
    const copy = async () => {
        await navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    const share = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: `Train with ${data.coach_name}`, text: `${data.coach_name} changed how I train. You'd love this. Use my link:`, url: link }); } catch {}
        } else { copy(); }
    };

    return (
        <div className="p-8 space-y-6" data-testid="client-referrals-page">
            <div>
                <p className="label-tiny mb-2">/ Refer a friend</p>
                <h1 className="font-serif text-6xl">Bring a friend.</h1>
                <p className="text-sm text-[var(--text-muted)] mt-2 max-w-xl">Share your link. Every friend who joins {data.coach_name} unlocks perks for you (your coach decides — free week, merch, programming credit).</p>
            </div>

            <div className="border border-[var(--gold)] bg-[var(--surface)] p-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-stripes opacity-25 pointer-events-none" />
                <div className="relative">
                    <p className="label-tiny mb-3 text-[var(--gold-bright)]">/ Your personal share link</p>
                    <div className="border border-[var(--border-bright)] bg-[var(--bg-deep)] px-3 py-3 font-mono text-xs text-[var(--gold-bright)] break-all mb-3" data-testid="my-share-link">
                        {link}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={copy} className="btn-ghost flex-1 inline-flex items-center justify-center gap-2" data-testid="copy-share-btn">
                            {copied ? <Check size={14} className="text-[var(--success)]" /> : <Copy size={14} />}
                            {copied ? "Copied" : "Copy"}
                        </button>
                        <button onClick={share} className="btn-primary flex-1 inline-flex items-center justify-center gap-2"><Share2 size={14} /> Share</button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)]">
                <div className="bg-[var(--surface)] p-6" data-testid="my-referred-count">
                    <div className="flex items-center justify-between mb-3">
                        <p className="label-tiny">Friends joined</p>
                        <Users size={16} className="text-[var(--gold)]" />
                    </div>
                    <p className="font-display text-6xl">{data.referred_count}</p>
                </div>
                <div className="bg-[var(--surface)] p-6">
                    <div className="flex items-center justify-between mb-3">
                        <p className="label-tiny">Perks earned</p>
                        <Gift size={16} className="text-[var(--gold-bright)]" />
                    </div>
                    <p className="text-sm text-[var(--text-muted)] mt-2">Your coach will reach out as you hit milestones.</p>
                </div>
            </div>

            {data.referred.length > 0 && (
                <div className="border border-[var(--border)] bg-[var(--surface)]">
                    <div className="p-5 border-b border-[var(--border)]">
                        <p className="label-tiny mb-1">/ Your referrals</p>
                        <h3 className="font-serif text-2xl">Friends you brought in</h3>
                    </div>
                    {data.referred.map((r) => (
                        <div key={r.id} className="px-5 py-3 border-b border-[var(--border)] last:border-b-0 flex items-center justify-between" data-testid={`referred-${r.id}`}>
                            <p className="font-medium">{r.name}</p>
                            <p className="font-mono text-xs text-[var(--text-muted)]">joined {new Date(r.created_at).toLocaleDateString()}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
